import React, { useState, useEffect, useCallback } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from '../../../../components/Providers/FloatMessage';
import { UpdateSuggestion } from '../../../../utilities/BL/Admin/Overlays/Playlist';

/**
 * Modal para edición rápida de metadatos de una sugerencia (título, artista, aficionado).
 * 
 * @component PlaylistEditSongModal
 * @param {Object} props
 * @param {boolean} props.isOpen - Controla la visibilidad del modal.
 * @param {Object|null} props.song - Sugerencia seleccionada para editar.
 * @param {Function} props.onClose - Callback al cerrar el modal.
 * @param {Function} props.onSuccess - Callback al actualizar con éxito.
 */
export const PlaylistEditSongModal = ({ isOpen, song, onClose, onSuccess }) => {
    const { showMessage } = useFloatMessage();

    const [title, setTitle] = useState('');
    const [artistName, setArtistName] = useState('');
    const [fanName, setFanName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Cargar datos al abrir
    useEffect(() => {
        if (isOpen && song) {
            setTitle(song.title || '');
            setArtistName(song.artistName || '');
            setFanName(song.fanName || '');
            setIsSubmitting(false);
        }
    }, [isOpen, song]);

    // Manejador tecla Escape
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape' && isOpen) {
            onClose();
        }
    }, [isOpen, onClose]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    if (!isOpen || !song) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();

        const cleanTitle = (title || '').trim();
        const cleanArtistName = (artistName || '').trim();
        const cleanFanName = (fanName || '').trim();

        if (!cleanTitle) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'El título de la pista no puede estar vacío.',
                message: 'El título de la pista no puede estar vacío.'
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const updatePayload = {
                title: cleanTitle,
                artistName: cleanArtistName || null,
                fanName: cleanFanName || null
            };

            const result = await UpdateSuggestion(song.id, updatePayload);

            if (result && result.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Pista "${cleanTitle}" actualizada correctamente.`,
                    message: `Pista "${cleanTitle}" actualizada correctamente.`
                });

                if (onSuccess && typeof onSuccess === 'function') {
                    onSuccess(result.data || { ...song, ...updatePayload });
                }
                onClose();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: (result && result.message) || 'Error al actualizar los datos de la sugerencia.',
                    message: (result && result.message) || 'Error al actualizar los datos de la sugerencia.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al procesar la actualización.',
                message: 'Error de conexión al procesar la actualización.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="PlaylistModal__Overlay" role="dialog" aria-modal="true">
            <div className="PlaylistModal__Backdrop" onClick={onClose} />
            <div className="PlaylistModal__Dialog">
                <div className="PlaylistModal__Content">
                    {/* Header */}
                    <div className="PlaylistModal__Header">
                        <div className="d-flex align-items-center gap-2">
                            <span className="PlaylistModal__HeaderIcon PlaylistModal__HeaderIcon--warning">
                                <i className="bi bi-pencil-square text-warning"></i>
                            </span>
                            <div>
                                <h4 className="PlaylistModal__Title mb-0">Editar Sugerencia</h4>
                                <p className="PlaylistModal__Subtitle mb-0">Modifica título, artista o nombre del aficionado</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="PlaylistModal__CloseButton"
                            onClick={onClose}
                            aria-label="Cerrar modal"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>

                    {/* Form Body */}
                    <form onSubmit={handleSubmit}>
                        <div className="PlaylistModal__Body">
                            <div className="PlaylistModal__Field mb-3">
                                <label className="PlaylistModal__Label" htmlFor="edit-song-title">
                                    Título de la Canción / Pista <span className="text-danger">*</span>
                                </label>
                                <div className="PlaylistModal__InputWrapper">
                                    <i className="bi bi-music-note-beamed PlaylistModal__InputIcon"></i>
                                    <input
                                        id="edit-song-title"
                                        type="text"
                                        className="PlaylistModal__Input"
                                        placeholder="Título de la pista"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        maxLength={100}
                                        required
                                        autoFocus
                                    />
                                </div>
                            </div>

                            <div className="PlaylistModal__Field mb-3">
                                <label className="PlaylistModal__Label" htmlFor="edit-song-artist">
                                    Artista / Banda
                                </label>
                                <div className="PlaylistModal__InputWrapper">
                                    <i className="bi bi-mic PlaylistModal__InputIcon"></i>
                                    <input
                                        id="edit-song-artist"
                                        type="text"
                                        className="PlaylistModal__Input"
                                        placeholder="Nombre del artista o agrupación"
                                        value={artistName}
                                        onChange={(e) => setArtistName(e.target.value)}
                                        maxLength={80}
                                    />
                                </div>
                            </div>

                            <div className="PlaylistModal__Field mb-1">
                                <label className="PlaylistModal__Label" htmlFor="edit-song-fan">
                                    Nombre del Aficionado / Solicitante
                                </label>
                                <div className="PlaylistModal__InputWrapper">
                                    <i className="bi bi-person-heart PlaylistModal__InputIcon"></i>
                                    <input
                                        id="edit-song-fan"
                                        type="text"
                                        className="PlaylistModal__Input"
                                        placeholder="Nombre del aficionado o apodo"
                                        value={fanName}
                                        onChange={(e) => setFanName(e.target.value)}
                                        maxLength={50}
                                    />
                                </div>
                                <span className="PlaylistModal__HelpText">
                                    Este nombre se proyectará opcionalmente en el estadio o pantalla gigante.
                                </span>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="PlaylistModal__Footer">
                            <button
                                type="button"
                                className="PlaylistModal__Btn PlaylistModal__Btn--secondary"
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="PlaylistModal__Btn PlaylistModal__Btn--warning"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Guardando...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check2 me-2"></i>
                                        Guardar Cambios
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PlaylistEditSongModal;
